import { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { MediaKind } from 'modules/manager/domain/media-kind';
import { BUILT_IN_VIDEO_SET_IDS, BUILT_IN_VIDEO_SET_LABELS, BuiltInVideoSetId } from 'modules/match-video/domain/constants';
import { ReviewPlatesMode } from 'modules/match-video/domain/interfaces';
import { VideoSetChoice, VideoTemplateGroups } from 'modules/match-video/ui/hooks';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import UDRadio from 'ud-form/components/radio';
import { UDSelect } from 'ud-form/components/select/component';
import { SelectOption } from 'ud-form/components/select/component/types';
import UDButton from 'ud-ui/components/button';
import Label from 'ud-ui/components/label';
import UDText from 'ud-ui/components/text';

const PLATES_LABELS: Record<ReviewPlatesMode, MessageDescriptor> = {
  set: msg`Из набора`,
  none: msg`Без плашек`,
};

/** Select values: one string per choice, so built-in ids and group ids never collide. */
const builtInValue = (id: BuiltInVideoSetId) => `built-in:${id}`;
const groupValue = (id: number) => `group:${id}`;

function parseValue(value: string): VideoSetChoice | null {
  const [kind, id] = value.split(':');
  if (kind === 'built-in') {
    const builtIn = BUILT_IN_VIDEO_SET_IDS.find((item) => item === id);
    return builtIn ? { kind: 'built-in', id: builtIn } : null;
  }
  const groupId = Number(id);
  return kind === 'group' && Number.isFinite(groupId) ? { kind: 'group', id: groupId } : null;
}

interface VideoSetPickerProps {
  groups: VideoTemplateGroups;
  plates: ReviewPlatesMode;
  onPlatesChange: (mode: ReviewPlatesMode) => void;
  /** The chosen set has plate templates — «Из набора» can be chosen. */
  hasPlates: boolean;
  disabled?: boolean;
}

/**
 * The set the intro and plates are drawn from, and whether the clips get plates. The built-in sets first (animated,
 * with plates), then own template groups and the global presets — the same split as the media manager's home page.
 */
const VideoSetPicker = ({ groups, plates, onPlatesChange, hasPlates, disabled }: VideoSetPickerProps) => {
  const { t } = useLingui();
  const navigate = useNavigate();

  const options = useMemo(
    () =>
      [
        {
          label: t`Стандартные наборы`,
          options: BUILT_IN_VIDEO_SET_IDS.map((id) => ({ label: t(BUILT_IN_VIDEO_SET_LABELS[id]), value: builtInValue(id) })),
        },
        { label: t`Ваши группы`, options: groups.own.map((group) => ({ label: group.name, value: groupValue(group.id) })) },
        { label: t`Общие шаблоны`, options: groups.presets.map((group) => ({ label: group.name, value: groupValue(group.id) })) },
      ].filter((section) => section.options.length > 0),
    [groups.own, groups.presets, t],
  );
  const selectedValue =
    groups.builtInId !== null ? builtInValue(groups.builtInId) : groups.selectedId !== null ? groupValue(groups.selectedId) : null;
  const selected = useMemo(
    () => options.flatMap((section) => section.options).find((option) => option.value === selectedValue) ?? null,
    [options, selectedValue],
  );
  const ownSelected = groups.own.find((group) => group.id === groups.selectedId);
  const noIntro = groups.selectedId !== null && !groups.isGroupLoading && !groups.isGroupError && !groups.intro;

  return (
    <div className="flex flex-col gap-4">
      <Label text={t`Набор шаблонов`}>
        <UDSelect<false>
          options={options}
          value={selected}
          onChange={(option) => {
            const choice = option && parseValue((option as SelectOption<string>).value);
            if (choice) groups.select(choice);
          }}
          isDisabled={disabled}
          aria-label={t`Набор шаблонов`}
          // `Label` is a column, so `basis-*` would set the height; the field is not Formik's, its width holds.
          divProps={{ className: 'w-80 max-w-full', 'data-testid': 'match-video-set' } as React.HTMLAttributes<HTMLDivElement>}
        />
      </Label>

      {groups.isGroupLoading && (
        <UDText type="footnote" role="status" className="text-inkSecondary">
          <Trans>Проверяем набор…</Trans>
        </UDText>
      )}
      {groups.isGroupError && (
        <div className="flex flex-wrap items-center gap-3">
          <UDText type="footnote" role="alert" className="text-CaseErrorText">
            <Trans>Не удалось загрузить шаблоны набора.</Trans>
          </UDText>
          <UDButton variant="secondary" type="button" onClick={groups.refetch}>
            <Trans>Повторить</Trans>
          </UDButton>
        </div>
      )}
      {noIntro && (
        <div className="flex flex-wrap items-center gap-3" data-testid="match-video-no-intro">
          <UDText type="footnote" role="status" className="text-CasePendingText">
            <Trans>В наборе нет шаблона «Результат матча» или «Расписание» — заставку не из чего нарисовать.</Trans>
          </UDText>
          {ownSelected && (
            <UDButton
              variant="secondary"
              type="button"
              onClick={() => navigate(Pages.MEDIA_MANAGER.builders.group(ownSelected.id, { create: MediaKind.MATCH_RESULT }))}
            >
              <Trans>Создать шаблон</Trans>
            </UDButton>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div role="radiogroup" aria-label={t`Плашки на клипах`} className="flex flex-wrap gap-4">
          {(Object.keys(PLATES_LABELS) as ReviewPlatesMode[]).map((mode) => {
            const isDisabled = disabled || (mode === 'set' && !hasPlates);
            return (
              <label
                key={mode}
                className={isDisabled ? 'flex cursor-not-allowed items-center gap-2' : 'flex cursor-pointer items-center gap-2'}
              >
                <UDRadio
                  name="review-plates"
                  value={mode}
                  checked={plates === mode}
                  disabled={isDisabled}
                  onChange={() => onPlatesChange(mode)}
                />
                <UDText type="subhead" className={isDisabled ? 'text-inkDisabled' : undefined}>
                  {t(PLATES_LABELS[mode])}
                </UDText>
              </label>
            );
          })}
        </div>
        {!hasPlates && (
          <UDText type="footnote" className="text-inkSecondary">
            <Trans>
              В этом наборе нет шаблонов «Плашка: гол» и «Плашка: опасный момент» — клипы пойдут без плашек. Плашки есть у стандартных
              наборов.
            </Trans>
          </UDText>
        )}
      </div>
    </div>
  );
};

export default VideoSetPicker;
